import { getLimitsChoropleth, getStorageDaysLimit } from '@protector/entities/choropleth/utils';
import { Entitlements } from '@protector/entities/licensing/constants';
import {
  CalculateCurrentInfoSuccess,
  CreateAreaVariablesMassivelyFailure,
  DeleteAreaVariableFailure,
  DeletePhenologyError,
  EditAreaVariablesMassivelyFailure,
  EditSeasonAreaDatesMassivelyFailure,
  EditSeasonAreaVarietiesMassivelyFailure,
  GetAreaDayRepresentativenessSuccess,
  GetAreaVariableFailure,
  GetAreaVariables,
  GetAreaVariablesSuccess,
  GetAreaVariableSuccess,
  GetRegionDataSuccess,
  LoadCurrentInfosByFieldsFailure,
  LoadCurrentInfosByFieldsSuccess,
  LoadRegion,
  LoadRegionDetailedInfo,
  LoadRegionDetailedInfoFailure,
  LoadRegionDetailedInfoSuccess,
  LoadRegionFailure,
  LoadRegionSeasonFields,
  LoadRegionSeasonFieldsFailed,
  LoadRegionSuccess,
  LoadWindowEventsFailure,
  LoadWindowEventsSuccess,
  PostAreaVariableFailure,
  PostAreaVariableSuccess,
  RegionActionsTypes,
  RegionAlreadyLoaded,
  ReloadRegionDetailedInfo,
  SetTimelineMapHighlightFilterSuccess,
  UpdateAreaVariableFailure,
  UpdateAreaVariableSuccess,
  UpdateRegionNematodeDemageSuccess,
  type CalculateCurrentInfo,
  type CreateAreaVariablesMassively,
  type DeleteAreaVariable,
  type DeletePhenology,
  type EditAreaVariablesMassively,
  type EditSeasonAreaDatesMassively,
  type EditSeasonAreaVarietiesMassively,
  type GetAreaDayRepresentativeness,
  type GetAreaVariable,
  type GetRegionData,
  type LoadCurrentInfosByFieldIds,
  type LoadWindowEvents,
  type PostAreaVariable,
  type ReloadRegion,
  type SetTimelineMapHighlightFilter,
  type UpdateAreaVariable,
  type UpdateRegionDaysMapsColors,
  type UpdateRegionPropertyNematodeDamage
} from '@protector/entities/region/__legacy__/redux.actions';
import {
  getParentRegion,
  selectAllRegions,
  selectRegionDictionaryByCurrentSeason,
  selectRegionEntities,
  selectRootRegion
} from '@protector/entities/region/__legacy__/redux.selectors';
import type { AppState } from '@protector/entities/__legacy__/redux.types';
import { SeasonAreaUpdateType } from '@protector/entities/region/constants';
import type { AreaVariable, Region, RegionGeometry } from '@protector/entities/region/models';
import {
  buildGeometries,
  getDateEditPayloadFromEditField,
  getRegionAreasWithParents,
  getRegionChildrenMapping,
  normalizeRegion,
  recalculateRegions,
  rootRegionWithChildren,
  toDeepRegion
} from '@protector/entities/region/utils';
import type { SeasonField } from '@protector/entities/season-field/models';
import type { Action } from '@protector/utils/types';
import { notification } from 'antd';
import type { AxiosError } from 'axios';
import i18n from 'config/i18n';
import { selectSelectedProperty } from 'core/core.reducer';
import { getLicensingStatus, getSelectedSeasons, getSystemFlags } from 'core/core.selectors';
import { selectShowScoutingScorePermission } from 'core/services/auth/auth.selectors';
import * as MapFunctions from 'core/shared/map/map.functions';
import { validatePlansAndEntitlements } from 'core/utils/functions';
import { reportOnce } from '@protector/api-client';
import { extractActiveSeasonsIds, filterSeasonsActive } from 'entities/season/season.functions';
import { getSelectedInactiveSeasonEndDate } from 'entities/season/season.reducer';
import { getSeasonEntitiesInEntities } from 'entities/season/season.selectors';
import { captureError } from 'infra/monitoring/monitoring.utils';
import _ from 'lodash';
import { removePhenologyFromTimelineWindowEvents } from 'pages/timeline/area-day-info/timeline.area-day-info.functions';
import { filterCropIdsFromSeasonBySeasonFields } from 'pages/timeline/area-info/hooks/use-area-info-drawer-variety-data.functions';
import type { TimelineWindowEvents } from 'pages/timeline/timeline.models';
import { deletePhenology, getWindowEvents } from 'pages/timeline/timeline.service';
import { transformWindowEventsResponse } from 'pages/timeline/timeline.utils';
import { getSeasonFieldFromPropertyBySeasonIdsObservable } from 'querys/season-field/season-field.service';
import { ofType, type ActionsObservable, type StateObservable } from 'redux-observable';
import { combineLatest, concat, forkJoin, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, mergeMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { GetAreasDiffJDOCFailure } from '../integrations/integrations.actions';
import { getSelectedProperty } from '../property/property.selectors';
import {
  fetchLatestScoutingScoresForDetailedInfo$,
  getCropsByIds,
  isSetAreaSeasonField,
  loadRegionSeasonFieldsSuccess
} from './region.epics.utils';
import * as Services from './region.service';

export const handleLoadRegionSeasonFields = (
  action$: ActionsObservable<ReturnType<typeof LoadRegionSeasonFields>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.LOAD_REGION_SEASON_FIELD),
    map(action => action.payload),
    withLatestFrom(
      state$.pipe(map(selectAllRegions)),
      state$.pipe(map(getParentRegion)),
      state$.pipe(map(getSeasonEntitiesInEntities)),
      state$.pipe(map(getSelectedSeasons))
    ),
    concatMap(([payload, regions, parentRegion, seasons, selectedSeasons]) => {
      if (!parentRegion) {
        return of(LoadRegionSeasonFieldsFailed());
      }

      return getSeasonFieldFromPropertyBySeasonIdsObservable({
        propertyIds: [payload.propertyId],
        seasonIds: selectedSeasons,
        update: payload.update
      }).pipe(
        mergeMap(seasonFields => {
          const regionChildrenMapping = getRegionChildrenMapping(regions);

          const areaSeasonFieldMap: Record<string, SeasonField> = {};
          seasonFields?.forEach(seasonField => {
            const isSetFieldInRecord = isSetAreaSeasonField(areaSeasonFieldMap, seasonField);
            if (isSetFieldInRecord) {
              areaSeasonFieldMap[seasonField.field_id] = seasonField;
            }
          });

          const cropIds = filterCropIdsFromSeasonBySeasonFields(seasons, seasonFields);

          return forkJoin(getCropsByIds(cropIds, payload.companyId)).pipe(
            mergeMap(
              loadRegionSeasonFieldsSuccess({
                regionChildrenMapping,
                region: parentRegion,
                areaSeasonFieldMap,
                selectedSeasons,
                seasons,
                cropIds
              })
            )
          );
        }),
        catchError(() => of(LoadRegionSeasonFieldsFailed()))
      );
    })
  );

export const handleEditAreaVariablesMassively = (
  action$: ActionsObservable<ReturnType<typeof EditAreaVariablesMassively>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.EDIT_AREA_VARIABLES_MASSIVELY),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSelectedSeasons))),
    mergeMap(([action, selectedSeasons]) => {
      const areaVariables = action.seasonAreas.map(el => ({
        ...action.areaVariable,
        season_area_id: el
      }));
      return Services.editAreaVariablesMassively(areaVariables, action.propertyId).pipe(
        tap(() => notification.success({ title: i18n.t('components.area_variable_info.save-success') })),
        map(() => GetAreaVariables(selectedSeasons)),
        catchError(() => {
          notification.error({ title: i18n.t('components.area_variable_info.error-save') });
          return of(EditAreaVariablesMassivelyFailure());
        })
      );
    })
  );

export const handleSaveAreaVariablesMassively = (
  action$: ActionsObservable<ReturnType<typeof CreateAreaVariablesMassively>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.CREATE_AREA_VARIABLES_MASSIVELY),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSelectedSeasons))),
    mergeMap(([action, selectedSeasons]) => {
      const areaVariables = action.seasonAreas.map(el => ({
        ...action.areaVariable,
        season_area_id: el
      }));
      return Services.createAreaVariablesMassively(areaVariables, action.propertyId).pipe(
        tap(() => notification.success({ title: i18n.t('components.area_variable_info.save-success') })),
        map(() => GetAreaVariables(selectedSeasons)),
        catchError(() => {
          notification.error({ title: i18n.t('components.area_variable_info.error-save') });
          return of(CreateAreaVariablesMassivelyFailure());
        })
      );
    })
  );

export const handleEditDatesMassively = (
  action$: ActionsObservable<ReturnType<typeof EditSeasonAreaDatesMassively>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.EDIT_SEASON_AREA_DATES_MASSIVELY),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSelectedProperty))),
    mergeMap(([payload, property]) => {
      const { seasonAreas, field, value, companyId } = payload;
      const formattedDate = value.format('YYYY-MM-DDTHH:mm:ssZZ');

      const payloadEditField = getDateEditPayloadFromEditField(field, formattedDate, seasonAreas);

      return Services.updateSeasonAreasMassively(payloadEditField).pipe(
        tap(() => notification.success({ title: i18n.t('components.area_variable_info.save-success') })),
        map(() => {
          return LoadRegionSeasonFields({ companyId, propertyId: property?.id ?? '', update: true });
        }),
        catchError(() => {
          notification.error({ title: i18n.t('components.area_variable_info.error-save') });
          return of(EditSeasonAreaDatesMassivelyFailure());
        })
      );
    })
  );

export const handleEditVarietiesMassively = (
  action$: ActionsObservable<ReturnType<typeof EditSeasonAreaVarietiesMassively>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.EDIT_SEASON_AREA_VARIETIES_MASSIVELY),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSelectedProperty))),
    mergeMap(([payload, property]) => {
      const { seasonAreas, varieties, companyId, selectedSeasons } = payload;

      const requestPayload = {
        model: {
          varieties
        },
        seasonIds: seasonAreas,
        fieldToUpdate: SeasonAreaUpdateType.VARIETIES
      };

      return Services.updateSeasonAreasMassively(requestPayload).pipe(
        tap(() => notification.success({ title: i18n.t('components.area_variable_info.save-success') })),
        map(() => {
          if (!property || !companyId || !selectedSeasons?.length) {
            return LoadRegionSeasonFieldsFailed();
          }

          return LoadRegionSeasonFields({ propertyId: property.id, companyId, update: true });
        }),
        catchError(() => {
          notification.error({ title: i18n.t('components.area_variable_info.error-save') });
          return of(EditSeasonAreaVarietiesMassivelyFailure());
        })
      );
    })
  );

export const handleLoadRegion = (action$: ActionsObservable<ReturnType<typeof LoadRegion>>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType(RegionActionsTypes.LOAD_REGION),
    map(action => action.payload),
    withLatestFrom(
      state$.pipe(map(getSelectedProperty)),
      state$.pipe(map(selectRegionEntities)),
      state$.pipe(map(state => state.entities.property)),
      state$.pipe(map(state => state.uiState.global)),
      state$.pipe(map(getSelectedInactiveSeasonEndDate))
    ),
    exhaustMap(
      ([
        props,
        selectedProperty,
        regionsEntities,
        { currentAreaSeasons, seasonAreasFromActiveSeasons: rawSeasonAreas, propertyData },
        { selectedSeasons, systemFlags },
        inactiveSeasonEndDate
      ]) => {
        if (rawSeasonAreas === undefined) {
          reportOnce(
            'region.epics:handleLoadRegion:seasonAreasFromActiveSeasons',
            () => new Error('region.epics: seasonAreasFromActiveSeasons missing -- handleLoadRegion fell back to {}'),
            { errorType: 'StateFallback', domain: 'Region' }
          );
        }
        if (!propertyData?.timeZone) {
          reportOnce(
            'region.epics:handleLoadRegion:propertyTimeZone',
            () => new Error('region.epics: propertyData.timeZone missing -- handleLoadRegion geometry reference dates fall back to UTC'),
            { errorType: 'StateFallback', domain: 'Region' }
          );
        }
        const seasonAreasFromActiveSeasons = rawSeasonAreas ?? {};
        const propertyId = props.propertyId ?? selectedProperty?.id ?? '';
        const seasons = selectedProperty?.seasons ?? [];
        const activeSeasonIds = filterSeasonsActive(seasons).map(s => s.id);
        const seasonIds = props?.seasonIds ?? activeSeasonIds;
        const useCurrentSeasonAreas =
          _.isEqual(new Set(activeSeasonIds), new Set(selectedSeasons)) && !!seasonAreasFromActiveSeasons[propertyId]?.length;
        if (!regionsEntities[props.regionId]) {
          return Services.getRegion(
            propertyId,
            seasons.filter(s => seasonIds.includes(s.id)),
            inactiveSeasonEndDate,
            seasonAreasFromActiveSeasons[propertyId],
            {
              useSeasonAreasFromActiveSeasons: useCurrentSeasonAreas,
              getGeometriesBySeasonFieldEndDate: false,
              includeArchived: !!systemFlags?.P40_59949_include_archived_fields,
              propertyTimeZone: propertyData?.timeZone,
              useFieldsBySeasonsEndpoint: !!systemFlags?.P40_61408_use_fields_by_seasons_endpoint,
              useFieldsBySeasonsCache: systemFlags?.P40_62642_use_fields_by_seasons_cache !== false
            }
          ).pipe(
            takeUntil(action$.pipe(ofType(RegionActionsTypes.RELOAD_REGION))),
            concatMap(recursiveRegion => {
              const recursiveRegionWithGeometry = buildGeometries(recursiveRegion, currentAreaSeasons, selectedSeasons);
              const normalizdRegions = normalizeRegion(recursiveRegionWithGeometry);
              return concat([
                LoadRegionSuccess(Object.values(normalizdRegions)),
                LoadRegionDetailedInfo({ regionId: props.regionId, propertyId, seasonIds })
              ]);
            }),
            catchError((error: unknown) => {
              captureError(error, { errorType: 'AxiosError', domain: 'Region' });
              return of(LoadRegionFailure(error));
            })
          );
        }
        return of(RegionAlreadyLoaded());
      }
    )
  );

export const handleReloadRegion = (action$: ActionsObservable<ReturnType<typeof ReloadRegion>>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType(RegionActionsTypes.RELOAD_REGION),
    map(action => {
      return action.payload;
    }),
    withLatestFrom(
      state$.pipe(map(state => selectSelectedProperty(state.uiState.global))),
      state$.pipe(map(selectRegionEntities)),
      state$.pipe(map(state => state.entities.property)),
      state$.pipe(map(state => state.uiState.global)),
      state$.pipe(map(getSelectedInactiveSeasonEndDate))
    ),
    concatMap(
      ([
        reloadRegionData,
        selectedPropertyId,
        regionsEntities,
        { currentAreaSeasons, seasonAreasFromActiveSeasons: rawSeasonAreas, entities: properties, propertyData },
        { selectedSeasons, systemFlags },
        inactiveSeasonEndDate
      ]) => {
        const seasonAreasFromActiveSeasons = rawSeasonAreas ?? {};
        if (!selectedPropertyId) return of();
        if (!reloadRegionData?.seasonIds) return of(LoadRegion(reloadRegionData.regionId));
        if (!propertyData?.timeZone) {
          reportOnce(
            'region.epics:handleReloadRegion:propertyTimeZone',
            () => new Error('region.epics: propertyData.timeZone missing -- handleReloadRegion geometry reference dates fall back to UTC'),
            { errorType: 'StateFallback', domain: 'Region' }
          );
        }
        if (!regionsEntities[reloadRegionData?.regionId]) {
          const { seasonIds } = reloadRegionData;
          const seasons = properties[selectedPropertyId]?.seasons || [];
          const activeSeasonIds = extractActiveSeasonsIds(seasons);
          const useSeasonAreasFromActiveSeasons =
            _.isEqual(new Set(activeSeasonIds), new Set(seasonIds)) && !!seasonAreasFromActiveSeasons[selectedPropertyId]?.length;

          return Services.getRegion(
            selectedPropertyId,
            seasons.filter(s => seasonIds?.includes(s.id)),
            inactiveSeasonEndDate,
            seasonAreasFromActiveSeasons[selectedPropertyId],
            {
              useSeasonAreasFromActiveSeasons,
              getGeometriesBySeasonFieldEndDate: true,
              includeArchived: !!systemFlags?.P40_59949_include_archived_fields,
              propertyTimeZone: propertyData?.timeZone,
              useFieldsBySeasonsEndpoint: !!systemFlags?.P40_61408_use_fields_by_seasons_endpoint,
              useFieldsBySeasonsCache: systemFlags?.P40_62642_use_fields_by_seasons_cache !== false
            }
          ).pipe(
            takeUntil(action$.pipe(ofType(RegionActionsTypes.RELOAD_REGION))),
            concatMap(recursiveRegion => {
              const recursiveRegionWithGeometry = buildGeometries(recursiveRegion, currentAreaSeasons, selectedSeasons);
              const normalizdRegions = normalizeRegion(recursiveRegionWithGeometry);
              return concat([
                LoadRegionSuccess(Object.values(normalizdRegions)),
                ReloadRegionDetailedInfo({
                  regionId: reloadRegionData.regionId,
                  propertyId: selectedPropertyId,
                  seasonIds: reloadRegionData.seasonIds ?? []
                })
              ]);
            }),
            catchError(error => {
              captureError(error, { errorType: 'AxiosError', domain: 'Region' });
              return of(LoadRegionFailure(error));
            })
          );
        }
        return of(RegionAlreadyLoaded());
      }
    )
  );

export const handleCalculateCurrentInfo = (
  action$: ActionsObservable<Action<typeof CalculateCurrentInfo>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.CALCULATE_CURRENT_INFO),
    map(action => action.payload),
    withLatestFrom(
      combineLatest([
        state$.pipe(map((state: AppState) => state.entities.region)),
        state$.pipe(map((state: AppState) => state.entities.property.currentAreaSeasons)),
        state$.pipe(map((state: AppState) => state.uiState.global.selectedSeasons)),
        state$.pipe(map((state: AppState) => state.entities.property.propertyData)),
        state$.pipe(map((state: AppState) => selectRootRegion(state)?.id)),
        state$.pipe(map((state: AppState) => getLicensingStatus(state)))
      ])
    ),
    exhaustMap(
      ([
        ,
        [
          { entities: regions, mapLayersColorDictionary, latestScoutingScoresPerField },
          currentAreaSeasons,
          selectedSeasons,
          propertyData,
          rootRegionId,
          licensingStatus
        ]
      ]) => {
        if (!rootRegionId) return of();

        const storageMaxDays = getStorageDaysLimit();

        const showMapSeedsLayer = validatePlansAndEntitlements(licensingStatus, null, [Entitlements.MAPS_SEEDS]);
        const showMapPhenologicalStageLayer = validatePlansAndEntitlements(licensingStatus, null, [Entitlements.MAP_PHENOLOGICAL_STAGE]);
        const showMapDAE = validatePlansAndEntitlements(licensingStatus, null, [Entitlements.MAP_DAE]);

        const rootRegion = rootRegionWithChildren(regions, rootRegionId);

        const choroplethLimits = getLimitsChoropleth(
          rootRegion.children as Region[],
          propertyData,
          currentAreaSeasons,
          selectedSeasons,
          storageMaxDays
        );

        const optionsMultilayer = {
          showMapSeedsLayer,
          showMapPhenologicalStageLayer,
          showMapDAE
        };

        const recursiveRegionWithGeometry = buildGeometries(
          rootRegion,
          currentAreaSeasons,
          selectedSeasons,
          mapLayersColorDictionary,
          choroplethLimits,
          storageMaxDays,
          optionsMultilayer
        );

        // buildGeometries rebuilds feature properties from scratch, dropping the scouting score data
        // applied during LOAD_REGION_DETAILED_INFO. Reapply it here using the scores already in the
        // store so the scouting score layer keeps its color (otherwise fill-color resolves to null).
        const regionWithScouting = latestScoutingScoresPerField
          ? {
              ...recursiveRegionWithGeometry,
              geometry: MapFunctions.addScoutingScoreToRegionProperties(
                recursiveRegionWithGeometry,
                latestScoutingScoresPerField
              ) as RegionGeometry
            }
          : recursiveRegionWithGeometry;

        const normalizedRegions = Object.values(normalizeRegion(regionWithScouting));

        return of(CalculateCurrentInfoSuccess({ regions: normalizedRegions, choroplethLimits }));
      }
    )
  );

export const handleLoadOrReloadRegionDetailedInfo = (
  action$: ActionsObservable<ReturnType<typeof LoadRegionDetailedInfo> | ReturnType<typeof ReloadRegionDetailedInfo>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.LOAD_REGION_DETAILED_INFO, RegionActionsTypes.RELOAD_REGION_DETAILED_INFO),
    map(action => action.payload),
    withLatestFrom(
      state$.pipe(map(state => state.entities.property.currentAreaSeasons)),
      state$.pipe(map(state => state.entities.property.propertyData)),
      state$.pipe(map(state => state.entities.region.entities)),
      state$.pipe(map(state => selectRootRegion(state)?.id)),
      state$.pipe(
        map(state => ({
          ...state.uiState.global,
          showScoutingScorePermission: selectShowScoutingScorePermission(state)
        }))
      )
    ),
    concatMap(
      ([
        props,
        currentAreaSeasons,
        propertyData,
        regions,
        rootRegionId,
        { selectedSeasons: selectedSeasonsIds, systemFlags, showScoutingScorePermission }
      ]) => {
        const { propertyId, seasonIds } = props;
        const listSeasons = seasonIds ?? selectedSeasonsIds;

        const currentInfo$ = Services.getPropertyCurrentInfo(propertyId, listSeasons);
        const scoutingScores$ = fetchLatestScoutingScoresForDetailedInfo$({
          propertyId,
          seasonIds: listSeasons,
          currentAreaSeasons,
          systemFlags,
          showScoutingScorePermission
        });

        return forkJoin([currentInfo$, scoutingScores$]).pipe(
          map(([currentInfoRegions, scoutingResult]) => {
            const { normalizedRegions, storageMaxDays, mapLayersColorDictionary } = recalculateRegions({
              currentInfoRegions,
              regions,
              rootRegionId,
              currentAreaSeasons,
              selectedSeasonsIds,
              propertyData,
              systemFlags,
              transformRootRegion: scoutingResult.kind === 'with-scores' ? scoutingResult.applyScoutingScores : undefined
            });

            return LoadRegionDetailedInfoSuccess({
              regions: Object.values(normalizedRegions),
              choroplethLimits: getLimitsChoropleth(
                normalizedRegions,
                propertyData,
                currentAreaSeasons,
                selectedSeasonsIds,
                storageMaxDays
              ),
              mapLayersColorDictionary,
              currentInfoRegions,
              latestScoutingScoresPerField: scoutingResult.kind === 'with-scores' ? scoutingResult.latestScoutingScoresPerField : undefined
            });
          }),
          catchError((error: unknown) => {
            captureError(error, { domain: 'Region' });
            return of(LoadRegionDetailedInfoFailure(String(error)));
          })
        );
      }
    )
  );

export const handleLoadWindowEvents = (
  action$: ActionsObservable<ReturnType<typeof LoadWindowEvents>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.LOAD_LATEST_WINDOW_EVENTS),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSystemFlags))),
    concatMap(([request, flags]) => {
      const showFlaming = !!flags?.P40_56666_enable_flaming_operation_type;
      const showEmergence = !!flags?.P40_57972_enable_emergence_timeline;
      return getWindowEvents(request.property_id, request.area_id, request.season_ids, request.start, request.end, {
        showFlaming,
        showEmergence
      }).pipe(
        map(response => {
          const enableMultiplePhotosNotes = !!flags?.P40_24588_enableMultiplePhotosNotes;

          if (!response.data) {
            return LoadWindowEventsFailure('Error loading events');
          }

          const windowData = transformWindowEventsResponse<TimelineWindowEvents>({
            response: response.data,
            enableMultiplePhotosNotes,
            request
          });

          return LoadWindowEventsSuccess(windowData);
        }),
        takeUntil(action$.pipe(ofType(RegionActionsTypes.CANCEL_LOAD_WINDOW_EVENTS))),
        catchError(() => of(LoadWindowEventsFailure('Error loading events')))
      );
    })
  );

export const handleLoadAreaDayRepresentativeness = (action$: ActionsObservable<ReturnType<typeof GetAreaDayRepresentativeness>>) => {
  return action$.pipe(
    ofType(RegionActionsTypes.GET_AREA_DAY_REPRESENTATIVENESS),
    map(action => action.payload),
    concatMap(({ monitoring, day, areaId, representedPointsIds, areaPointsIds }) => {
      const areasIds = new Set(
        monitoring
          .filter(m => representedPointsIds.includes(m.id) || (m.represented_by && areaPointsIds.includes(m.represented_by)))
          .map(m => m.area_id)
      );
      return of(GetAreaDayRepresentativenessSuccess({ id: areaId, day, coverageGroup: areasIds }));
    })
  );
};

export const handleLoadFieldsCurrentInfoBatch = (
  action$: ActionsObservable<ReturnType<typeof LoadCurrentInfosByFieldIds>>,
  state$: StateObservable<AppState>
) => {
  return action$.pipe(
    ofType(RegionActionsTypes.LOAD_CURRENT_INFOS_BY_FIELD_IDS),
    map(action => action.payload),
    withLatestFrom(
      state$.pipe(map(state => selectRegionDictionaryByCurrentSeason(state))),
      state$.pipe(map(state => state.entities.property.currentAreaSeasons)),
      state$.pipe(map(state => state.uiState.global.selectedSeasons)),
      state$.pipe(map(state => state.entities.property.propertyData))
    ),
    concatMap(([{ propertyId, fieldIds, seasonIds, selectedRegion }, regions, currentAreaSeasons, selectedSeasons, propertyData]) => {
      return Services.getCurrentInfosByFieldIdsPaginated(propertyId, fieldIds, seasonIds).pipe(
        map(currentInfos => {
          const currentInfoMap = Object.fromEntries(currentInfos.flat().map(c => [c.field_id, c.current_info]));
          const selectedRegionNormalized = regions[selectedRegion];
          const selectedRegionDeep = toDeepRegion(selectedRegionNormalized, currentInfoMap, regions);
          const childAreas = getRegionAreasWithParents(selectedRegionDeep);
          const storageMaxDays = getStorageDaysLimit();
          const recursiveRegionWithGeometry = buildGeometries(
            selectedRegionDeep,
            currentAreaSeasons,
            selectedSeasons,
            {},
            getLimitsChoropleth(childAreas, propertyData, currentAreaSeasons, selectedSeasons, storageMaxDays),
            storageMaxDays
          );
          const normalizedRegions = normalizeRegion(recursiveRegionWithGeometry);
          return LoadCurrentInfosByFieldsSuccess(Object.values(normalizedRegions));
        }),
        catchError((error: string) => of(LoadCurrentInfosByFieldsFailure(error)))
      );
    })
  );
};

export const handleUpdateDaysMapColors = (
  action$: ActionsObservable<ReturnType<typeof UpdateRegionDaysMapsColors>>,
  state$: StateObservable<AppState>
) => {
  return action$.pipe(
    ofType(RegionActionsTypes.UPDATE_REGION_DAYS_MAP_COLORS),
    withLatestFrom(
      state$.pipe(map(state => state.entities.region.entities)),
      state$.pipe(map(state => state.entities.property)),
      state$.pipe(map(state => selectRootRegion(state)?.id)),
      state$.pipe(map(state => state.entities.region.currentInfoRegions)),
      state$.pipe(map(state => state.uiState.global))
    ),
    concatMap(
      ([
        ,
        regions,
        { currentAreaSeasons, propertyData },
        rootRegionId,
        currentInfoRegions,
        { selectedSeasons: selectedSeasonsIds, systemFlags }
      ]) => {
        if (!currentInfoRegions) return of();

        const { normalizedRegions, storageMaxDays, mapLayersColorDictionary } = recalculateRegions({
          currentInfoRegions,
          regions,
          rootRegionId,
          currentAreaSeasons,
          selectedSeasonsIds,
          propertyData,
          systemFlags
        });

        return of(
          LoadRegionDetailedInfoSuccess({
            regions: Object.values(normalizedRegions),
            choroplethLimits: getLimitsChoropleth(normalizedRegions, propertyData, currentAreaSeasons, selectedSeasonsIds, storageMaxDays),
            mapLayersColorDictionary,
            currentInfoRegions
          })
        );
      }
    )
  );
};

export const handleUpdateRegionsNematodeDamage = (
  action$: ActionsObservable<ReturnType<typeof UpdateRegionPropertyNematodeDamage>>,
  state$: StateObservable<AppState>
) => {
  return action$.pipe(
    ofType(RegionActionsTypes.UPDATE_REGION_NEMATODE_DAMAGE),
    map(action => action.payload),
    withLatestFrom(
      state$.pipe(map(state => state.entities.region.entities)),
      state$.pipe(map(state => state.uiState.global.selectedRegion))
    ),
    concatMap(([nemadigitalReport, regions, selectedRegionId]) => {
      const rootRegion = rootRegionWithChildren(regions, selectedRegionId ?? '');
      const newRegions = {
        ...rootRegion,
        geometry: MapFunctions.addNematodeDamageToRegionProperties(
          rootRegion,
          nemadigitalReport.nematodeDamage,
          nemadigitalReport.isUSAUsers,
          nemadigitalReport.hiddenFieldsIds,
          nemadigitalReport.lastSoySeasonByField
        ) as RegionGeometry
      };
      const normalizedRegions = normalizeRegion(newRegions);
      return of(UpdateRegionNematodeDemageSuccess({ regions: Object.values(normalizedRegions) }));
    })
  );
};

export const handleSetTimelineFilter = (
  action$: ActionsObservable<ReturnType<typeof SetTimelineMapHighlightFilter>>,
  state$: StateObservable<AppState>
) => {
  return action$.pipe(
    ofType(RegionActionsTypes.TIMELINE_MAP_HIGHLIGHT_FILTER),
    map(action => action.payload),
    withLatestFrom(
      state$.pipe(map(state => state.entities.region.entities)),
      state$.pipe(map(state => state.uiState.global.selectedRegion))
    ),
    concatMap(([highlightFilter, regions, selectedRegionId]) => {
      const rootRegion = rootRegionWithChildren(regions, selectedRegionId ?? '');
      const newRegions = {
        ...rootRegion,
        geometry: MapFunctions.updateRegionsHighlightStatus(rootRegion, highlightFilter) as RegionGeometry
      };
      const normalizedRegions = normalizeRegion(newRegions);
      return of(SetTimelineMapHighlightFilterSuccess({ regions: Object.values(normalizedRegions) }));
    })
  );
};

export const handleGetAreaVariable = (action$: ActionsObservable<ReturnType<typeof GetAreaVariable>>) =>
  action$.pipe(
    ofType(RegionActionsTypes.GET_AREA_VARIABLE),
    map(action => action.payload),
    concatMap(payload => {
      return Services.getAreaVariable(payload).pipe(
        map(response => {
          return GetAreaVariableSuccess(response);
        }),
        catchError((error: string) => of(GetAreaVariableFailure(error)))
      );
    })
  );

export const handleGetAreaVariables = (action$: ActionsObservable<ReturnType<typeof GetAreaVariables>>) =>
  action$.pipe(
    ofType(RegionActionsTypes.GET_AREA_VARIABLES),
    map(action => action.payload),
    concatMap(({ seasonIds }: { seasonIds: string[] }) => {
      return forkJoin(seasonIds.map(seasonId => Services.getAreaVariable(seasonId))).pipe(
        map(responses => {
          const areaVariablesPerSeasonArea: Record<string, AreaVariable[]> = {};
          seasonIds.forEach((seasonId, idx) => {
            areaVariablesPerSeasonArea[seasonId] = responses[idx];
          });
          return GetAreaVariablesSuccess(areaVariablesPerSeasonArea);
        })
      );
    })
  );

export const handlePostAreaVariable = (
  action$: ActionsObservable<ReturnType<typeof PostAreaVariable>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.POST_AREA_VARIABLE),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(state => selectSelectedProperty(state.uiState.global)))),
    mergeMap(([payload, selectedProperty]) => {
      return Services.postAreaVariable(selectedProperty, payload).pipe(
        map(response => {
          return PostAreaVariableSuccess(response);
        }),
        catchError((error: AxiosError<{ message?: string }>) => {
          if (error.response?.data?.message?.includes('already present')) {
            notification.error({ title: i18n.t('components.area_variable_info.error-duplicated-variable') });
          } else {
            notification.error({ title: i18n.t('components.area_variable_info.error-save') });
          }
          return of(PostAreaVariableFailure(error as unknown as string));
        })
      );
    })
  );

export const handleUpdateAreaVariable = (
  action$: ActionsObservable<ReturnType<typeof UpdateAreaVariable>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.UPDATE_AREA_VARIABLE),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(state => selectSelectedProperty(state.uiState.global)))),
    mergeMap(([payload, selectedProperty]) => {
      return Services.updateAreaVariable(selectedProperty, payload).pipe(
        map(response => {
          return UpdateAreaVariableSuccess(response);
        }),
        catchError((error: string) => of(UpdateAreaVariableFailure(error)))
      );
    })
  );

export const handleDeleteAreaVariable = (
  action$: ActionsObservable<ReturnType<typeof DeleteAreaVariable>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(RegionActionsTypes.DELETE_AREA_VARIABLE),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSelectedSeasons))),
    mergeMap(([payload, selectedSeasons]) => {
      return Services.deleteAreaVariable(payload.areaVariables, payload.companyId, payload.propertyId).pipe(
        mergeMap(() => {
          notification.success({ title: i18n.t('components.area_variable_info.delete-success') });
          return of(GetAreaVariables(selectedSeasons));
        }),
        catchError(() => {
          notification.error({ title: i18n.t('components.area_variable_info.error-delete') });
          return of(DeleteAreaVariableFailure(''));
        })
      );
    })
  );

export const handleGetRegionData = (action$: ActionsObservable<ReturnType<typeof GetRegionData>>) =>
  action$.pipe(
    ofType(RegionActionsTypes.GET_REGION_DATA),
    map(action => action.payload),
    mergeMap(payload => {
      return Services.getRegionData(payload).pipe(
        map(result => {
          return GetRegionDataSuccess(result);
        }),
        catchError((error: string) => of(GetAreasDiffJDOCFailure(error)))
      );
    })
  );

export const handleDeletePhenology = (action$: ActionsObservable<ReturnType<typeof DeletePhenology>>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType(RegionActionsTypes.DELETE_PHENOLOGY),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map(getSystemFlags))),
    mergeMap(([payload]) => {
      if (!payload) {
        return of(DeletePhenologyError('Payload not provided'));
      }

      const { sample } = payload;

      return deletePhenology(sample.id).pipe(
        mergeMap(() => {
          notification.success({
            title: i18n.t('pages.timeline.phenology.delete_sample.message'),
            description: i18n.t('pages.timeline.phenology.delete_sample.success_description')
          });

          removePhenologyFromTimelineWindowEvents({
            dataSample: sample
          });
          return of();
        }),
        catchError((error: string) => {
          notification.error({
            title: i18n.t('pages.timeline.phenology.delete_sample.message'),
            description: i18n.t('pages.timeline.phenology.delete_sample.error_description')
          });

          return of(DeletePhenologyError(error));
        })
      );
    })
  );
